<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { NxBadge, NxButton, NxKeyValueList, NxPanel, NxTextInput, type NxKeyValueItem } from '@blpde/nx-ui';
import { useSearchDictionaryClient, type ExplainLevel } from '../../search/dictionaryApi';
import { DICTIONARY_TEXT, explainLevelLabel, explainResult } from './labels';

const props = defineProps<{
  /** Checked once when the panel opens (the term or terms being edited). */
  initialQuery?: string;
}>();
const { t } = useI18n();
const client = useSearchDictionaryClient();
const T = `${DICTIONARY_TEXT}.explain`;

const ORDER = ['account', 'company', 'tenant', 'module', 'base'] as const;
const probe = ref(props.initialQuery?.trim() ?? '');
const levels = ref<ExplainLevel[]>([]);
const checked = ref(false);

const wins = computed(() => new Set<string>(levels.value.filter((level) => level.wins).map((level) => level.level)));

const result = computed(() => explainResult(t, levels.value, checked.value));

const items = computed<NxKeyValueItem[]>(() => [
  ...ORDER.map((level, index) => {
    const found = levels.value.find((item) => item.level === level);
    const value = !checked.value
      ? '—'
      : found && found.contributes.length
        ? found.contributes.join(' · ')
        : t(`${T}.empty`);
    return {
      key: level,
      label: `${index + 1} · ${explainLevelLabel(t, level)}`,
      value,
      full: true,
    };
  }),
  ...(result.value === undefined ? [] : [{ key: 'result', label: t(`${T}.result`), value: result.value, full: true }]),
]);

async function run(): Promise<void> {
  levels.value = await client.explain(probe.value.trim());
  checked.value = true;
}

// A panel that opens on a term shows what the search makes of it right away.
onMounted(() => {
  if (!probe.value) return;
  void run().catch(() => {
    // The button asks again.
  });
});
</script>

<template>
  <NxPanel :title="t(`${T}.title`)" data-testid="dictionary-explain">
    <div class="dict-probe">
      <NxTextInput v-model="probe" :aria-label="t(`${T}.probe`)" />
      <NxButton variant="outline" @click="run">{{ t(`${T}.check`) }}</NxButton>
    </div>
    <p class="dict-note">{{ t(`${T}.rule`) }}</p>
    <NxKeyValueList :items="items" layout="stacked" dense>
      <template #value="{ item }">
        {{ item.value }}
        <NxBadge v-if="wins.has(String(item.key))" variant="success" size="xs">
          {{ t(`${T}.wins`) }}
        </NxBadge>
      </template>
    </NxKeyValueList>
  </NxPanel>
</template>

<style scoped>
.dict-probe {
  display: flex;
  gap: var(--nxt-space-xs);
  align-items: center;
  margin-bottom: var(--nxt-space-xs);
}
.dict-probe > :first-child {
  flex: 1;
}
.dict-note {
  margin: 0 0 var(--nxt-space-xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
</style>
